import { JobDtoType } from '../domain/jobs';
import { IEventRequestParams, IPaginationParams } from './shared.dto';
import { IControlRoomDTO, OperatorStatus } from './control.dao';
import { IAttendeeOrganizationDTO } from './attendees.dao';
import { IConnectionDetailsDTO, IConnectionGroupDTO } from './connections.dao';
import { OperatorRole } from './operators.dao';
import { IUserDTO } from './user-groups.dao';
import { ConnectionVersion } from '../constants/common.constants';

abstract class IRoomsDAO {
    public abstract createRoom(accountId: number, eventId: number, createRoomDTO: IRoomSaveDTO): Promise<void>;
    public abstract getRoomsList(params: IEventRequestParams & IPaginationParams & { query: string }): Promise<IRoomListDTO>;
    public abstract getRoomById(params: { accountId: number; eventId: number; roomId: number }): Promise<IRoomDetailsDTO>;
    public abstract updateRoom(accountId: number, eventId: number, roomId: number, updateRoomDTO: IRoomSaveDTO): Promise<void>;
    public abstract deleteRoom(params: { accountId: number; eventId: number; roomId: number}): Promise<void>;
    public abstract createZoomConnections(params: IEventRequestParams & { zoomGroupId: number; additionalTopicText: string }): Promise<JobDtoType>;
    public abstract generateRooms(generateRoomsDTO: IGenerateRoomsDTO & IEventRequestParams): Promise<JobDtoType>;
    public abstract getRoomsNarrowed(params: IEventRequestParams & IRoomNarrowedSortParams): Promise<IRoomNarrowed[]>;
    public abstract importOperators(data: IImportOperatorsRequestParams & IEventRequestParams): Promise<JobDtoType>;
    public abstract getOperatorsCSV(params: IEventRequestParams): void;
    public abstract updateRoomInEvent(params: IUpdateRoomDTO): Promise<IControlRoomDTO>;
}

interface IImportOperatorsRequestParams {
    file: File;
}

interface IGenerateRoomsDTO {
    endTime: Date;
    startTime: Date;
    connectionVersion: ConnectionVersion;
}

interface IUpdateRoomDTO extends IRoomInput, IEventRequestParams {
    roomId: number;
    requestedTime?: string;
}

interface IGenerateRoomsResultDTO {
    createdRoomsCount: number;
    meetingsAssignedToRoomsCount: number;
    meetingsWithoutRoomsCount: number;
}

interface IRoomSaveDTO {
    endDateTime: Date;
    startDateTime: Date;
    addedOperators?: IOperatorDetailsDTO[];
    removedOperators?: IOperatorDetailsDTO[];
    updatedOperators?: IOperatorDetailsDTO[];
    presenters: IAttendeeOrganizationDTO[];
    presenterRoleConnectionId: number | null;
    label?: string;
    hostRoleConnectionId?: number | null;
    connectionVersion?: ConnectionVersion;
}

interface IRoomNarrowed {
    startDateTime: Date;
    endDateTime: Date;
    presenters: IAttendeeOrganizationDTO[];
    name: string;
    label: string;
    roomId: number;
    connectionVersion?: ConnectionVersion;
}

interface IRoomNarrowedSortParams {
    startDateTime?: Date;
    endDateTime?: Date;
    presenter?: number[];
}

interface IOperatorInput {
    operatorId: number;
    operatorStatus?: OperatorStatus;
}

interface IRoomInput {
    roomOperators?: IOperatorInput[];
    meetingConnections?: IConnectionDetailsDTO[];
}

interface IRoomDTO extends IRoomInput {
    roomId: number;
    eventId?: number;
    roomName: string;
    roomLabel: string;
    operators: IOperatorDetailsDTO[];
    presenters?: IAttendeeOrganizationDTO[];
    defaultConnection?: IConnectionDetailsDTO | null;
    presentersConnection?: IConnectionDetailsDTO | null;
    hostsConnection?: IConnectionDetailsDTO | null;
}

interface IOperatorDetailsDTO {
    firstName: string;
    lastName: string;
    fullName?: string;
    email: string;
    operatorId: number;
    operatorStatus?: OperatorStatus;
    operatorRole: OperatorRole | null;
    user?: IUserDTO;
    active?: boolean;
}

interface IRoomDetailsDTO {
    name: string;
    label: string;
    roomId: number;
    endDateTime: Date;
    presenters: IAttendeeOrganizationDTO[];
    startDateTime: Date;
    operators?: IOperatorDetailsDTO[];
    connections?: IConnectionDetailsDTO[];
    presenterRoleConnectionId?: number | null;
    defaultConnectionId?: number | null;
    hostRoleConnectionId?: number | null;
    connectionVersion?: ConnectionVersion;
    connectionGroups?: IConnectionGroupDTO[];
}

interface IRoomListDTO {
    totalElements: number;
    content: IRoomDetailsDTO[];
}

export {
    IRoomsDAO,
    IRoomListDTO,
    IRoomDetailsDTO,
    IRoomSaveDTO,
    IGenerateRoomsDTO,
    IRoomNarrowed,
    IRoomNarrowedSortParams,
    IGenerateRoomsResultDTO,
    IImportOperatorsRequestParams,
    IOperatorDetailsDTO,
    IRoomInput,
    IRoomDTO,
    IUpdateRoomDTO
};
